// Angular
import { Component, Input, OnChanges, SimpleChanges, OnInit } from '@angular/core';
import { ValidatorFn } from '@angular/forms';
import { FieldSpecsLayout, IFieldSpecs } from '../interfaces/field-specs';
import { IErrorConfig, IOneErrorConfig } from './config/error.config';
import { IValidator } from '../../forms/utils/validators/IValidator';
import { faCircle, faExclamationTriangle, faQuestion, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { Required } from "libraries/module/formbuilder/forms/utils/validators/required.validator";
import { RequiredIf } from "libraries/module/formbuilder/forms/utils/validators/requiredIf.validator";

export enum ELabelPosition {
    top = "top",
    right = "right",
    left = "left",
}

/**
 * Component that wrap field (includes label + error's gestion)
 * example of use :
 *  <form-field [options]="{
 *    formGroup: form,
 *    fieldControlName: 'toto',
 *    className: 'flex-2 search',
 *    label: 'myLabel'
 *  }">
 *   <text-field
 *     [group]="form" class="flex-2 search" icon="fa fa-search" controlName="toto" placeholder="Rechercher"></text-field>
 *  </form-field>
 */
@Component({
    selector: 'form-field',
    templateUrl: './form-field.component.html',
    styleUrls: ['./form-field.component.scss']
})
export class FormFieldComponent implements OnChanges, OnInit {

    @Input()
    public options: IFieldSpecs;

    @Input()
    public isFull: boolean;
    @Input()
    public isInternalError: boolean;

    @Input()
    public labelPosition: ELabelPosition = ELabelPosition.top;

    public labelPositions: typeof ELabelPosition = ELabelPosition;

    public iconQuestion: IconDefinition = faQuestion;
    public iconCircle: IconDefinition = faCircle;
    public iconExclamation: IconDefinition = faExclamationTriangle;

    public showDescription: boolean = false;
    public fieldSpecsLayout: typeof FieldSpecsLayout = FieldSpecsLayout;

    public configErrors: IErrorConfig;
    public hasLabel: boolean = false;

    public get cErrors(): Array<{ key: string, config: IOneErrorConfig }> {
        const validatorData: IValidator[] | undefined = this.options.validators;
        const errors: IErrorConfig = (validatorData || [])
            .map((validator: IValidator): IErrorConfig => validator.errors)
            .reduce((aggregator: IErrorConfig, validatorErrors: IErrorConfig): IErrorConfig => ({...aggregator, ...validatorErrors}), {});

        return Object.entries(errors)
            .map(([key, config]: [string, IOneErrorConfig]): {key: string, config: IOneErrorConfig} => ({key, config}));
    }

    public ngOnInit(): void {
        this.hasLabel = Boolean(this.options.label);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const previousOptions: IFieldSpecs = changes.options.previousValue;
        const currentOptions: IFieldSpecs = changes.options.currentValue;

        const previousValidators: IValidator[] | undefined = previousOptions && previousOptions.validators;
        const currentValidators: IValidator[] | undefined = currentOptions && currentOptions.validators;

        if (previousValidators !== currentValidators || changes.options.firstChange) {
            this.setValidators();
        }

        const validatorData: IValidator[] | undefined = this.options.validators;
        this.configErrors = (validatorData || [])
            .map((validator: IValidator): IErrorConfig => validator.errors)
            .reduce((aggregator: IErrorConfig, validatorErrors: IErrorConfig): IErrorConfig => ({...aggregator, ...validatorErrors}), {});
    }

    public findError(key: string): IOneErrorConfig{
        return this.configErrors[key] || {};
    }

    public toggleDescription(): void {
        this.showDescription = !this.showDescription;
    }

    public isFieldRequired(): boolean {
        return Boolean(this.options.validators.find((value: IValidator): boolean => value instanceof Required));
    }

    private setValidators(): void {
        if (!this.options) {
            return;
        }

        const currentValidators: IValidator[] | undefined = this.options.validators;

        const controlValidators: ValidatorFn[] = (currentValidators || []).map((validator: IValidator) => validator.validator);
        this.options.formGroup.get(this.options.formControlName)?.setValidators(controlValidators);
    }
}
